import { cva } from 'class-variance-authority';
import Link from 'next/link';
import React, { MouseEventHandler, forwardRef, type ForwardedRef } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { cn } from '@/utils/ui';

import { ButtonVariant, VARIANT } from './variant';

export interface ButtonType {
  className?: string;
  href?: string;
  variant?: ButtonVariant;
  id?: string;
  size?: 'sm' | 'md' | 'lg' | 'rlg' | 'rsm' | 'pT';
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  style?: React.CSSProperties;
  dataAnalytics: string;
  role?: React.AriaRole;
  title?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonType>(function Button(
  {
    children,
    className = '',
    href,
    variant = 'transparent',
    type = 'button',
    size = 'pT',
    onClick,
    isLoading = false,
    disabled: disabledProps,
    style,
    /* To determine a meaningful name for the data-analytics attribute, use a descriptive,
    kebab-case name that reflects the button's specific action and context (e.g., content-detail-watch-button),
    starting with the page or feature name and avoiding generic terms. */
    dataAnalytics,
    role,
    ...rest
  },
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
) {
  const darkColors = ['dark', 'navy', 'navyAccent', 'black', 'cancel'];
  const loaderColor = darkColors.includes(variant) ? 'white' : 'black';

  const disabled = isLoading ? true : disabledProps;

  const buttonVariants = cva('items-center justify-center rounded-[5px] capitalize duration-200', {
    variants: {
      variant: {
        ...VARIANT,
        roundWhite: cn(VARIANT.roundWhite, disabled ? 'bg-paleSlate' : 'bg-white'),
      },
      size: {
        sm: 'px-[15px] py-[10px] text-[14px] leading-[15px]',
        md: 'text-[16px]',
        lg: 'h-[55px] w-[360px] font-FoxOneAeroBold text-[18px]',
        rlg: 'size-[44px]',
        rsm: 'size-[24px]',
        pT: '',
      },
    },
    defaultVariants: {
      variant: 'transparent',
      size: 'lg',
    },
  });

  if (href && !disabled) {
    return (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        className={cn('inline-flex no-underline hover:no-underline', buttonVariants({ variant, size }), className)}
        data-analytics={dataAnalytics}
        data-testid={`${variant}-button`}
        href={href}
        onClick={onClick}
        style={style}
        {...rest}
      >
        {isLoading ? <Icon className="size-[20px] animate-spin" color={loaderColor} id="loader" /> : children}
      </Link>
    );
  }

  return (
    <button
      ref={ref as ForwardedRef<HTMLButtonElement>}
      className={cn('flex', buttonVariants({ variant, size }), className)}
      data-analytics={dataAnalytics}
      data-testid={`${variant}-button`}
      onClick={onClick}
      type={type}
      disabled={disabled}
      style={style}
      {...rest}
    >
      {isLoading ? (
        <Icon className="pointer-events-auto size-[20px] animate-spin" color={loaderColor} id="loader" />
      ) : (
        children
      )}
    </button>
  );
});

Button.displayName = 'Button';
